"use client";
import React from "react";
import { PartyPopper } from "lucide-react";
import FormProvider, { useFormik, Yup } from "providers/FormProvider";
import { TEAL, CATEGORIES, PROVINCES } from "./constant";
import { TextField, TextareaField, SelectField } from "./Field";
import { useT } from "providers/I18nProvider";

/** Giá trị form hợp tác — key trùng với payload /api/partner */
export interface PartnerFormValues {
  shopName: string;
  contactName: string;
  phone: string;
  email: string;
  category: string;
  website: string;
  address: string;
  province: string;
  note: string;
  /** Honeypot chống bot: người thật không thấy, không điền */
  company_url: string;
}

export const PARTNER_INITIAL_VALUES: PartnerFormValues = {
  shopName: "",
  contactName: "",
  phone: "",
  email: "",
  category: "",
  website: "",
  address: "",
  province: "",
  note: "",
  company_url: "",
};

/** SĐT Việt Nam: 0xxxxxxxxx (10 số) hoặc +84xxxxxxxxx; cho phép khoảng trắng/dấu chấm/gạch */
const VN_PHONE = /^(\+84|0)[\s.-]?[1-9](?:[\s.-]?\d){8}$/;

/** Tạo schema theo locale — thông báo lỗi phải đổi theo i18n (t truyền từ component, Yup không có context React). */
const createPartnerSchema = (t: (key: string) => string) =>
  Yup.object({
    shopName: Yup.string().trim().required(t("partner.errShopNameRequired")).max(120),
    contactName: Yup.string().trim().max(80),
    phone: Yup.string()
      .trim()
      .required(t("partner.errPhoneRequired"))
      .matches(VN_PHONE, t("partner.errPhoneInvalid")),
    email: Yup.string().trim().email(t("partner.errEmailInvalid")).max(120),
    category: Yup.string(),
    website: Yup.string().trim().max(200),
    address: Yup.string().trim().max(200),
    province: Yup.string().trim().max(80),
    note: Yup.string().trim().max(500, t("partner.errNoteMax")),
    company_url: Yup.string(),
  });



export type SubmitState = { status: "idle" | "success" | "error"; errorMsg?: string };

interface Props {
  state: SubmitState;
  onSubmit: (values: PartnerFormValues) => Promise<void>;
  onReset: () => void;
}

const SuccessBox: React.FC<{ onReset: () => void; t: (key: string) => string }> = ({ onReset, t }) => (
  <div className="rounded-2xl bg-[#eefbfb] border border-[#d8f3f2] p-8 text-center">
    <PartyPopper size={40} strokeWidth={1.8} className="mx-auto" style={{ color: TEAL }} aria-hidden="true" />
    <p className="mt-2 font-extrabold text-lg !mb-0" style={{ color: TEAL }}>
      {t("partner.successTitle")}
    </p>
    <p className="text-sm text-gray-600 mt-1">{t("partner.successDesc")}</p>
    <button
      type="button"
      onClick={onReset}
      className="mt-4 rounded-full border px-5 py-2 text-sm font-bold"
      style={{ borderColor: TEAL, color: TEAL }}
    >
      {t("partner.successReset")}
    </button>
  </div>
);

/** Form đăng ký hợp tác — Formik + Yup, validate phía client trước khi gọi /api/partner */
const PartnerForm: React.FC<Props> = ({ state, onSubmit, onReset }) => {
  const t = useT();
  const partnerSchema = React.useMemo(() => createPartnerSchema(t), [t]);
  const form = useFormik<PartnerFormValues>({
    initialValues: PARTNER_INITIAL_VALUES,
    validationSchema: partnerSchema,
    onSubmit: async (values, helpers) => {
      await onSubmit(partnerSchema.cast(values) as PartnerFormValues);
      helpers.setSubmitting(false);
    },
  });

  // Gửi xong thành công → xoá form để lần "Gửi đăng ký khác" bắt đầu trống
  React.useEffect(() => {
    if (state.status === "success") form.resetForm();
  }, [state.status]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state.status === "success") return <SuccessBox onReset={onReset} t={t} />;

  return (
    <FormProvider form={form}>
      <form className="relative space-y-4" onSubmit={form.handleSubmit} noValidate>
        {/* Honeypot chống bot: ẩn khỏi mắt + tab; server trả ok giả nếu có giá trị */}
        <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
          <label htmlFor="company_url">Company URL</label>
          <input
            id="company_url"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            {...form.getFieldProps("company_url")}
          />
        </div>

        <TextField name="shopName" label={t("partner.fieldShopName")} required maxLength={120} placeholder={t("partner.fieldShopNamePlaceholder")} />

        <div className="grid sm:grid-cols-2 gap-4">
          <TextField name="contactName" label={t("partner.fieldContactName")} maxLength={80} placeholder={t("partner.fieldContactNamePlaceholder")} autoComplete="name" />
          <TextField
            name="phone"
            label={t("partner.fieldPhone")}
            required
            type="tel"
            inputMode="tel"
            maxLength={20}
            placeholder={t("partner.fieldPhonePlaceholder")}
            autoComplete="tel"
          />
        </div>

        <TextField name="email" label={t("partner.fieldEmail")} type="email" maxLength={120} placeholder={t("partner.fieldEmailPlaceholder")} autoComplete="email" />

        <SelectField
          name="category"
          label={t("partner.fieldCategory")}
          options={CATEGORIES.map((c) => ({ value: c.value, label: t(c.labelKey) }))}
          emptyLabel={t("partner.fieldCategoryEmpty")}
        />

        <TextField
          name="website"
          label={t("partner.fieldWebsite")}
          maxLength={200}
          placeholder={t("partner.fieldWebsitePlaceholder")}
        />

        <div className="grid sm:grid-cols-2 gap-4">
          <TextField name="address" label={t("partner.fieldAddress")} maxLength={200} placeholder={t("partner.fieldAddressPlaceholder")} />
          <div>
            <TextField name="province" label={t("partner.fieldProvince")} maxLength={80} list="province-list" placeholder={t("partner.fieldProvincePlaceholder")} />
            <datalist id="province-list">
              {PROVINCES.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
          </div>
        </div>

        <TextareaField name="note" label={t("partner.fieldNote")} rows={3} maxLength={500} placeholder={t("partner.fieldNotePlaceholder")} />

        {state.status === "error" && (
          <p role="alert" className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600 !mb-0">
            {state.errorMsg}
          </p>
        )}

        <button
          type="submit"
          disabled={form.isSubmitting}
          className="w-full rounded-full py-3.5 font-bold text-white shadow-md transition-transform hover:scale-[1.01] active:scale-95 disabled:opacity-60 disabled:hover:scale-100"
          style={{ background: TEAL }}
        >
          {form.isSubmitting ? t("partner.submitting") : t("partner.submitBtn")}
        </button>
        <p className="text-[12px] text-gray-400 text-center !mb-0">
          {t("partner.formFooterNote")}
        </p>
      </form>
    </FormProvider>
  );
};

export default PartnerForm;
